import { useQuery } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { fetchAppointmentSuggestions, fetchAppointments } from '../../api/appointments';
import { fetchBranches } from '../../api/salon';
import AppointmentDetailDrawer from '../../components/appointments/AppointmentDetailDrawer';
import StaffAssignmentBadge from '../../components/appointments/StaffAssignmentBadge';
import StatusBadge from '../../components/appointments/StatusBadge';
import DashboardShell from '../../components/ui/DashboardShell';
import DatePicker from '../../components/ui/DatePicker';
import EmptyState from '../../components/ui/EmptyState';
import { TableRowSkeleton } from '../../components/ui/Skeleton';
import { appointmentToEvent } from '../../utils/appointmentStatus';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { getPaymentPolicy, slipRequired, slipAllowsUpload } from '../../utils/paymentSlip';

const MANAGER_STATUS_OPTIONS = ['PENDING', 'APPROVED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];
const STAFF_OWN_STATUS_OPTIONS = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];

const SalonAppointmentsPage = () => {
  const { t } = useTranslation();

  const paymentBadge = (appointment) => {
    const policy = getPaymentPolicy(appointment);
    if (!slipAllowsUpload(policy) && !appointment.payment?.requiresAdvance) return null;
    const verified = appointment.paymentSlips?.some((s) => s.isVerified);
    if (verified) return { label: t('orders.payment.verified'), className: 'text-emerald-400' };
    if (appointment.paymentSlips?.length) return { label: t('orders.payment.slipUploaded'), className: 'text-amber-400' };
    if (slipRequired(policy)) return { label: t('payments.slipRequired'), className: 'text-red-400' };
    return { label: t('orders.payment.awaiting'), className: 'text-zinc-600 dark:text-zinc-500' };
  };
  const { user, role, tenantId } = useAuth();
  const salons = user?.salons || [];
  const multiSalonStaff = role === 'SALON_STAFF' && salons.length > 1;
  const canManageAppointments =
    role === 'SALON_MANAGER'
    || role === 'SUPER_ADMIN'
    || user?.canManageAppointments
    || user?.permissions?.canManageAppointments;
  const staffOwnView = role === 'SALON_STAFF' && !canManageAppointments;
  const statusOptions = staffOwnView ? STAFF_OWN_STATUS_OPTIONS : MANAGER_STATUS_OPTIONS;

  const [view, setView] = useState('calendar');
  const [calendarHeight, setCalendarHeight] = useState(650);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [salonFilter, setSalonFilter] = useState(tenantId || '');
  const [branchFilter, setBranchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (!tenantId) return;
    setSalonFilter((prev) => (prev === 'all' ? prev : tenantId));
  }, [tenantId]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const sync = () => setCalendarHeight(mq.matches ? 650 : 480);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const salonIdParam = multiSalonStaff
    ? salonFilter === 'all'
      ? 'all'
      : salonFilter || tenantId
    : undefined;

  const showBranchFilter = !multiSalonStaff || salonFilter !== 'all';

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['appointment-suggestions', statusFilter, salonFilter, branchFilter, dateFrom, dateTo],
    fetchSuggestions: (q) =>
      fetchAppointmentSuggestions({
        q,
        status: statusFilter || undefined,
        salonId: salonIdParam,
        branchId: showBranchFilter ? branchFilter || undefined : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
  });

  const { data: branchesData } = useQuery({
    queryKey: ['salon-branches'],
    queryFn: fetchBranches,
    enabled: showBranchFilter,
  });

  const listLimit = view === 'calendar' ? 500 : pageSize;

  const { data, isLoading } = useQuery({
    queryKey: ['appointments', view, page, pageSize, statusFilter, salonFilter, branchFilter, dateFrom, dateTo, appliedSearch],
    queryFn: () =>
      fetchAppointments({
        page: view === 'list' ? page : 1,
        limit: listLimit,
        status: statusFilter || undefined,
        salonId: salonIdParam,
        branchId: showBranchFilter ? branchFilter || undefined : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: appliedSearch || undefined,
      }),
    staleTime: 30 * 1000,
  });

  const appointments = useMemo(() => data?.appointments || [], [data]);
  const pagination = data?.pagination;
  const events = useMemo(() => appointments.map(appointmentToEvent), [appointments]);

  return (
    <DashboardShell
      title={staffOwnView ? t('appointments.myAppointments') : t('appointments.title')}
      subtitle={staffOwnView ? t('appointments.myAppointmentsSubtitle') : t('appointments.salonSubtitle')}
    >
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-800 p-1">
          <button
            type="button"
            onClick={() => { setView('calendar'); setPage(1); }}
            className={`rounded-md px-3 py-1.5 text-sm ${view === 'calendar' ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400'}`}
          >
            {t('appointments.listTabCalendar')}
          </button>
          <button
            type="button"
            onClick={() => { setView('list'); setPage(1); }}
            className={`rounded-md px-3 py-1.5 text-sm ${view === 'list' ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400'}`}
          >
            {t('appointments.listTabList')}
          </button>
        </div>
      </div>

      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={staffOwnView ? t('appointments.filters.searchMyAppointments') : t('appointments.filters.searchClient')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
      >
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('appointments.filters.allStatuses')}</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{t(`status.appointment.${s}`)}</option>
          ))}
        </select>
        {multiSalonStaff && (
          <select
            value={salonFilter}
            onChange={(e) => {
              setSalonFilter(e.target.value);
              setBranchFilter('');
              setPage(1);
            }}
            className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
          >
            <option value="all">{t('common.allSalons')}</option>
            {salons.map((s) => (
              <option key={s.tenantId} value={s.tenantId}>{s.businessName}</option>
            ))}
          </select>
        )}
        {showBranchFilter && (
          <select value={branchFilter} onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
            <option value="">{t('appointments.filters.allBranches')}</option>
            {(branchesData?.branches || []).map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        )}
        <DatePicker id="appt-from" label={t('common.from')} compact value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <DatePicker id="appt-to" label={t('common.to')} compact value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
      </ListSearchToolbar>

      {view === 'calendar' && (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white p-2 dark:border-zinc-800 dark:bg-zinc-900 sm:p-4 [&_.fc]:text-zinc-700 dark:[&_.fc]:text-zinc-300 [&_.fc-toolbar]:flex-wrap [&_.fc-toolbar]:gap-2 [&_.fc-toolbar-title]:text-base sm:[&_.fc-toolbar-title]:text-xl">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            height={calendarHeight}
            events={events}
            eventClick={(info) => setSelectedId(info.event.id)}
          />
        </div>
      )}

      {view === 'list' && (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
              <tr>
                <th className="px-4 py-3">{t('appointments.table.client')}</th>
                <th className="px-4 py-3">{t('appointments.table.service')}</th>
                <th className="px-4 py-3">{t('appointments.table.branch')}</th>
                <th className="px-4 py-3">{t('appointments.preferredColumn')}</th>
                {!staffOwnView && <th className="px-4 py-3">{t('appointments.table.staff')}</th>}
                <th className="px-4 py-3">{t('appointments.table.status')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={staffOwnView ? 5 : 6} />)}
              {!isLoading && appointments.length === 0 && (
                <tr>
                  <td colSpan={staffOwnView ? 5 : 6}>
                    <EmptyState
                      icon="📅"
                      title={t('empty.noAppointments')}
                      subtitle={staffOwnView ? t('appointments.myEmptySubtitle') : t('appointments.salonEmptySubtitle')}
                    />
                  </td>
                </tr>
              )}
              {appointments.map((a) => (
                <tr
                  key={a._id}
                  onClick={() => setSelectedId(a._id)}
                  className="cursor-pointer border-b border-zinc-200/50 dark:border-zinc-800/50 hover:bg-zinc-200/30 dark:bg-zinc-800/30"
                >
                  <td className="px-4 py-3">{a.clientName}</td>
                  <td className="px-4 py-3">{a.serviceName}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">{a.branchName}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                    {new Date(a.preferredDate).toLocaleDateString()} {a.preferredTime}
                  </td>
                  {!staffOwnView && (
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                      {a.assignedStaffId ? `${a.assignedStaffId.firstName} ${a.assignedStaffId.lastName}` : '—'}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={a.status} />
                      {(() => {
                        const pay = paymentBadge(a);
                        return pay ? <span className={`text-xs ${pay.className}`}>{pay.label}</span> : null;
                      })()}
                      {a.staffAssignmentStatus && (
                        <StaffAssignmentBadge status={a.staffAssignmentStatus} />
                      )}
                      {a.cancellationReason && (
                        <p className="mt-1 max-w-xs text-xs text-red-400/90" title={a.cancellationReason}>
                          {a.status === 'CANCELLED' ? `${t('appointments.cancellationClientPrefix')} ` : ''}
                          {a.cancellationReason}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginatedFooter
            pagination={pagination}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        </div>
      )}

      <AppointmentDetailDrawer
        appointmentId={selectedId}
        onClose={() => setSelectedId(null)}
        clientMode={false}
      />
    </DashboardShell>
  );
};

export default SalonAppointmentsPage;
